import { useQuery } from '@tanstack/react-query';
import { ArrowRight } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';
import { useTranslation } from 'react-i18next';

import { ChurchCombobox } from '@/components/church-combobox';
import { DataTable, type DataTableColumn } from '@/components/shared/data-table';
import { DetailField } from '@/components/shared/detail-field';
import { FormField } from '@/components/shared/form-field';
import { PageHeader } from '@/components/shared/page-header';
import { Pagination } from '@/components/shared/pagination';
import { Badge, type BadgeVariant } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { DatePicker } from '@/components/ui/date-picker';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Listbox } from '@/components/ui/listbox';
import { Spinner } from '@/components/ui/spinner';
import { api, type OffsetPage } from '@/lib/api';
import { formatPlatformAuditChanges } from '@/lib/platform-audit-changes';
import { PLATFORM_AUDIT_ACTIONS } from '@/lib/platform-audit-actions';
import { formatDateTime } from '@/utils/format';

interface AuditRow {
  id: string;
  action: string;
  summary: string;
  severity: 'info' | 'notable' | 'critical';
  entityType: string;
  entityId: string | null;
  churchId: string | null;
  churchName: string | null;
  actorUserId: string | null;
  actorName: string | null;
  reason: string | null;
  createdAt: string;
}

interface AuditDetail extends AuditRow {
  metadata: Record<string, unknown> | null;
  beforeValue: Record<string, unknown> | null;
  afterValue: Record<string, unknown> | null;
}

const SEVERITIES = ['info', 'notable', 'critical'] as const;

const severityVariant = (severity: AuditRow['severity']): BadgeVariant => {
  if (severity === 'critical') return 'danger';
  if (severity === 'notable') return 'warning';
  return 'neutral';
};

const AuditChangeList = ({
  beforeValue,
  afterValue,
  empty,
}: {
  beforeValue: Record<string, unknown> | null;
  afterValue: Record<string, unknown> | null;
  empty: string;
}) => {
  const { t } = useTranslation('admin');
  const changes = formatPlatformAuditChanges(beforeValue, afterValue, t);

  if (changes.length === 0) {
    return <p className="mt-1 text-body-sm text-fg">{empty}</p>;
  }

  return (
    <dl className="mt-2 space-y-3 rounded-md border border-border bg-surface-sunken p-3">
      {changes.map((change) => (
        <div key={change.key} className="min-w-0">
          <dt className="text-caption font-medium text-fg-secondary">{change.label}</dt>
          <dd className="mt-0.5 flex min-w-0 flex-wrap items-center gap-x-1.5 gap-y-1 text-body-sm text-fg">
            {change.before ? (
              <>
                <span className="break-words text-fg-tertiary line-through">{change.before}</span>
                <ArrowRight aria-hidden className="h-3 w-3 shrink-0 text-fg-tertiary" />
              </>
            ) : null}
            <span className="break-words">{change.after ?? empty}</span>
          </dd>
        </div>
      ))}
    </dl>
  );
};

export const AuditPage = () => {
  const { t } = useTranslation('admin');
  const { t: tCommon } = useTranslation('common');
  const [searchParams, setSearchParams] = useSearchParams();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const action = searchParams.get('action') ?? '';
  const severity = searchParams.get('severity') ?? '';
  const churchId = searchParams.get('churchId') ?? '';
  const dateFrom = searchParams.get('dateFrom') ?? '';
  const dateTo = searchParams.get('dateTo') ?? '';

  const setFilter = (key: string, value: string | null) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value);
    else next.delete(key);
    setSearchParams(next, { replace: true });
    setPage(1);
  };

  const list = useQuery({
    queryKey: ['platform-audit', page, pageSize, action, severity, churchId, dateFrom, dateTo],
    queryFn: () =>
      api.get<{ data: AuditRow[]; page: OffsetPage }>('/platform/audit', {
        query: {
          page,
          pageSize,
          sort: '-createdAt',
          ...(action ? { action } : {}),
          ...(severity ? { severity } : {}),
          ...(churchId ? { churchId } : {}),
          ...(dateFrom ? { dateFrom } : {}),
          ...(dateTo ? { dateTo } : {}),
        },
      }),
  });

  const detail = useQuery({
    queryKey: ['platform-audit-entry', selectedId],
    queryFn: () => api.get<AuditDetail>(`/platform/audit/${selectedId}`),
    enabled: Boolean(selectedId),
  });

  const actionOptions = useMemo(
    () => [
      { value: '', label: t('audit.allActions') },
      ...PLATFORM_AUDIT_ACTIONS.map((value) => ({
        value,
        label: t(`audit.actions.${value}`),
      })),
    ],
    [t],
  );

  const severityOptions = useMemo(
    () => [
      { value: '', label: t('audit.allSeverities') },
      ...SEVERITIES.map((value) => ({ value, label: t(`audit.severity.${value}`) })),
    ],
    [t],
  );

  const calendarLabels = useMemo(
    () => ({
      month: t('churches.detail.calendarMonth'),
      year: t('churches.detail.calendarYear'),
      previousMonth: t('churches.detail.calendarPrevious'),
      nextMonth: t('churches.detail.calendarNext'),
    }),
    [t],
  );

  const actionLabel = (value: string) =>
    (PLATFORM_AUDIT_ACTIONS as readonly string[]).includes(value)
      ? t(`audit.actions.${value as (typeof PLATFORM_AUDIT_ACTIONS)[number]}`)
      : value;

  const columns: DataTableColumn<AuditRow>[] = [
    {
      id: 'createdAt',
      header: t('audit.columns.when'),
      cell: (row) => formatDateTime(row.createdAt),
    },
    {
      id: 'action',
      header: t('audit.columns.action'),
      cell: (row) => actionLabel(row.action),
    },
    {
      id: 'church',
      header: t('audit.columns.church'),
      cell: (row) => row.churchName ?? t('audit.emptyValue'),
      hideBelowMd: true,
    },
    {
      id: 'actor',
      header: t('audit.columns.actor'),
      cell: (row) => row.actorName ?? t('audit.emptyValue'),
      hideBelowMd: true,
    },
    {
      id: 'severity',
      header: t('audit.columns.severity'),
      cell: (row) => (
        <Badge variant={severityVariant(row.severity)}>{t(`audit.severity.${row.severity}`)}</Badge>
      ),
    },
  ];

  const pageMeta = list.data?.page;
  const selected = detail.data;

  return (
    <div>
      <PageHeader title={t('audit.title')} description={t('audit.description')} />

      <div className="mb-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <FormField label={t('audit.columns.action')}>
          {(field) => (
            <Listbox
              {...field}
              value={action}
              onChange={(value) => setFilter('action', value || null)}
              options={actionOptions}
            />
          )}
        </FormField>
        <FormField label={t('audit.columns.severity')}>
          {(field) => (
            <Listbox
              {...field}
              value={severity}
              onChange={(value) => setFilter('severity', value || null)}
              options={severityOptions}
            />
          )}
        </FormField>
        <FormField label={t('audit.searchChurch')}>
          {(field) => (
            <ChurchCombobox
              id={field.id}
              value={churchId || null}
              onChange={(value) => setFilter('churchId', value)}
              placeholder={t('audit.searchChurchPlaceholder')}
              emptyMessage={t('audit.searchChurchEmpty')}
              clearLabel={t('audit.searchChurchAny')}
            />
          )}
        </FormField>
        <div className="grid grid-cols-2 gap-4">
          <FormField label={t('audit.dateFrom')}>
            {(field) => (
              <DatePicker
                id={field.id}
                value={dateFrom || null}
                onChange={(value) => setFilter('dateFrom', value)}
                calendarLabels={calendarLabels}
                clearable
                clearLabel={t('billing.coupons.clearDate')}
              />
            )}
          </FormField>
          <FormField label={t('audit.dateTo')}>
            {(field) => (
              <DatePicker
                id={field.id}
                value={dateTo || null}
                onChange={(value) => setFilter('dateTo', value)}
                calendarLabels={calendarLabels}
                clearable
                clearLabel={t('billing.coupons.clearDate')}
              />
            )}
          </FormField>
        </div>
      </div>

      {action || severity || churchId || dateFrom || dateTo ? (
        <div className="mb-4 flex justify-end">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => {
              setSearchParams({}, { replace: true });
              setPage(1);
            }}
          >
            {t('audit.clearFilters')}
          </Button>
        </div>
      ) : null}

      <DataTable
        rows={list.data?.data ?? []}
        columns={columns}
        rowKey={(row) => row.id}
        loading={list.isLoading}
        onRowClick={(row) => setSelectedId(row.id)}
        empty={<p className="py-8 text-center text-body-sm text-fg-secondary">{t('audit.empty')}</p>}
      />

      {pageMeta && pageMeta.totalPages > 1 ? (
        <div className="mt-4">
          <Pagination
            page={pageMeta.page}
            pageSize={pageMeta.pageSize}
            totalItems={pageMeta.totalItems}
            totalPages={pageMeta.totalPages}
            onPageChange={setPage}
            onPageSizeChange={(next) => {
              setPageSize(next);
              setPage(1);
            }}
            labels={{
              showing: ({ from, to, total }) => tCommon('pagination.showing', { from, to, total }),
              perPage: tCommon('pagination.perPage'),
              first: tCommon('pagination.first'),
              previous: tCommon('pagination.previous'),
              next: tCommon('pagination.next'),
              last: tCommon('pagination.last'),
            }}
          />
        </div>
      ) : null}

      <Dialog open={selectedId !== null} onOpenChange={(open) => !open && setSelectedId(null)}>
        <DialogContent closeLabel={tCommon('action.close')} size="md">
          <DialogHeader>
            <DialogTitle>{t('audit.detailTitle')}</DialogTitle>
            <DialogDescription>{t('audit.detailDescription')}</DialogDescription>
          </DialogHeader>
          <DialogBody contentClassName="flex flex-col gap-4">
            {detail.isLoading || !selected ? (
              <div className="flex justify-center py-8">
                <Spinner className="size-6 text-brand" />
              </div>
            ) : (
              <dl className="grid gap-4 sm:grid-cols-2">
                <DetailField stacked label={t('audit.columns.when')}>
                  {formatDateTime(selected.createdAt)}
                </DetailField>
                <DetailField stacked label={t('audit.columns.action')}>
                  {actionLabel(selected.action)}
                </DetailField>
                <DetailField stacked label={t('audit.columns.church')}>
                  {selected.churchName ?? t('audit.emptyValue')}
                </DetailField>
                <DetailField stacked label={t('audit.columns.actor')}>
                  {selected.actorName ?? t('audit.emptyValue')}
                </DetailField>
                <DetailField stacked label={t('audit.columns.severity')}>
                  <Badge variant={severityVariant(selected.severity)}>
                    {t(`audit.severity.${selected.severity}`)}
                  </Badge>
                </DetailField>
                <DetailField stacked label={t('audit.columns.reason')}>
                  {selected.reason ?? t('audit.emptyValue')}
                </DetailField>
                <div className="min-w-0 sm:col-span-2">
                  <p className="text-body-sm text-fg-secondary">{t('audit.changes')}</p>
                  <AuditChangeList
                    beforeValue={selected.beforeValue}
                    afterValue={selected.afterValue}
                    empty={t('audit.emptyValue')}
                  />
                </div>
              </dl>
            )}
          </DialogBody>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSelectedId(null)}>
              {tCommon('action.close')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
